import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";

import {
    AlarmSeverity,
    AlarmSeverityCount,
    AlarmSeverityGuid,
    AlarmStatus,
    AlarmStatusCount,
    AlarmStatusInfo,
    JSONAlarmCategoryStatistics,
    JSONAlarmSeverity,
    JSONAlarmStatus,
} from "../data/AlarmsMonitorData";

declare var AppTranslations: AppTranslations;

export class AlarmsMonitorDataMapper {

    public static toAlarmStatusesInfo(statuses: JSONAlarmStatus[]): AlarmStatusInfo[] {
        return statuses.map(
            (status) => {
                return AlarmsMonitorDataMapper.toAlarmStatusInfo(status);
            },
        );
    }

    public static toAlarmStatusInfo(status: JSONAlarmStatus): AlarmStatusInfo {
        return {
            statusId: status.code,
            status: AlarmsMonitorDataMapper.toAlarmsStatusEnum(status.code),
            name: status.name,
            image: status.image,
        };
    }

    public static toAlarmsStatusEnum(code: number): AlarmStatus {
        switch (code) {
            case 0:
                return AlarmStatus.NOT_IN_CHARGE;
            case 1:
                return AlarmStatus.IN_CHARGE;
            case 2:
                return AlarmStatus.RECENTLY_CLOSED;
        }
        return AlarmStatus.CLOSED;
    }

    public static toAlarmStatusCode(status: AlarmStatus): number {
        if (status == null) {
            return null;
        }
        switch (status) {
            case AlarmStatus.NOT_IN_CHARGE:
                return 0;
            case AlarmStatus.IN_CHARGE:
                return 1;
            case AlarmStatus.RECENTLY_CLOSED:
                return 2;
        }
        return 3;
    }

    public static toAlarmStatusesCount(statuses: JSONAlarmStatus[],
                                       data: JSONAlarmCategoryStatistics[],
                                       severity?: AlarmSeverity): AlarmStatusCount[] {
        const countsMap = new Map();
        if (data != null && data.length > 0) {
            data.forEach(
                (element) => {
                    if (element.statusStatistics != null && element.statusStatistics.length > 0) {
                        element.statusStatistics.forEach(
                            (stat) => {
                                if (countsMap.get(stat.statusId) == null) {
                                    countsMap.set(stat.statusId, 0);
                                }
                                if (severity == null) {
                                    countsMap.set(stat.statusId, countsMap.get(stat.statusId) + stat.count);
                                } else {
                                    if (stat.severitiesCount != null && stat.severitiesCount.length > 0) {
                                        stat.severitiesCount.forEach(
                                            (severityCount) => {
                                                if (this.toAlarmsSeverityEnum(
                                                    severityCount.severityGuid,
                                                ) === severity) {
                                                    countsMap.set(
                                                        stat.statusId,
                                                        countsMap.get(stat.statusId) + severityCount.count,
                                                    );
                                                }
                                            },
                                        );
                                    }
                                }
                            },
                        );
                    }
                },
            );
        }
        const results: AlarmStatusCount[] = [];
        statuses.forEach(
            (element) => {
                let count = 0;
                if (countsMap.get(element.code) !== null) {
                    count = countsMap.get(element.code);
                }
                results.push(
                    {
                        statusInfo: AlarmsMonitorDataMapper.toAlarmStatusInfo(element),
                        count,
                    },
                );
            },
        );
        return results;
    }

    public static toAlarmsSeverityEnum(severityGuid: string): AlarmSeverity {
        switch (severityGuid) {
            case AlarmSeverityGuid.URGENT:
                return AlarmSeverity.URGENT;
            case AlarmSeverityGuid.HIGH:
                return AlarmSeverity.HIGH;
        }
        return AlarmSeverity.WARNING;
    }

    public static toAlarmsSeverityGuid(severity: AlarmSeverity): string {
        if (severity == null) {
            return null;
        }
        switch (severity) {
            case AlarmSeverity.URGENT :
                return AlarmSeverityGuid.URGENT;
            case AlarmSeverity.HIGH:
                return AlarmSeverityGuid.HIGH;
        }
        return AlarmSeverityGuid.WARNING;
    }

    public static toAlarmSeveritiesCount(severities: JSONAlarmSeverity[], data: JSONAlarmCategoryStatistics[]) {
        const countsMap = new Map();
        if (data != null && data.length > 0) {
            data.forEach(
                (element) => {
                    if (element.statusStatistics != null && element.statusStatistics.length > 0) {
                        element.statusStatistics.forEach(
                            (stat) => {
                                if (stat.severitiesCount != null && stat.severitiesCount.length > 0) {
                                    stat.severitiesCount.forEach(
                                        (severity) => {
                                            let count = 0;
                                            if (countsMap.get(severity.severityGuid) != null) {
                                                count = countsMap.get(severity.severityGuid);
                                            }
                                            countsMap.set(severity.severityGuid, count + severity.count);
                                        },
                                    );
                                }
                            },
                        );
                    }
                },
            );
        }
        const results: AlarmSeverityCount[] = [];
        severities.forEach(
            (element) => {
                let count = 0;
                if (countsMap.get(element.guid) !== null) {
                    count = countsMap.get(element.guid);
                }
                results.push(
                    {
                        severityInfo: element,
                        count,
                    },
                );
            },
        );
        return results;
    }

    public static getSeverityColor(severities: JSONAlarmSeverity[], severityGuid: string) {
        for (const severity of severities) {
            if (severity.guid === severityGuid) {
                return severity.color;
            }
        }
        return null;
    }

    public static getSeverityBorderColor(severities: JSONAlarmSeverity[], severityGuid: string) {
        for (const severity of severities) {
            if (severity.guid === severityGuid) {
                return severity.borderColor;
            }
        }
        return null;
    }

    public static getAlarmStatusImage(alarmStatus: AlarmStatus) {
        switch (alarmStatus) {
            case AlarmStatus.NOT_IN_CHARGE:
                return "iconAlarm-OpenWihtOutWo.png";
            case AlarmStatus.IN_CHARGE:
                return "iconAlarm-workOrderAlarm.png";
            default:
                return "iconAlarm-alarmClose.png";
        }
    }

    public static getMaxCount(data: JSONAlarmCategoryStatistics[],
                              statusCode: number,
                              severityGuid: string): Map<string, number> {
        const max = new Map();
        for (const severity of AppTranslations.alarmsMonitor.severities) {
            max.set(severity.guid, 0);
        }
        if (data == null || data.length === 0) {
            return max;
        }
        for (const cat of data) {
            if (cat.statusStatistics == null || cat.statusStatistics.length === 0) {
                continue;
            }
            for (const status of cat.statusStatistics) {
                if (status.count === 0 || status.statusId !== statusCode) {
                    continue;
                }
                for (const severity of status.severitiesCount) {
                    if (severityGuid != null && severity.severityGuid !== severityGuid) {
                        continue;
                    }
                    if (severity.count > max.get(severity.severityGuid)) {
                        max.set(severity.severityGuid, severity.count);
                    }
                }
            }
        }
        return max;
    }
}
